import React, { FC, useState, useEffect } from "react";
import { Table, Select, InputNumber, Space, Empty, Button } from "antd";
import Icon, {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { Alert } from "reactstrap";
import { tasksApis } from "../../../api/NewTasks";
import { ReactTyped } from "react-typed";
import {
  AddAccess,
  EditAccess,
  CancelAccess,
} from "../../../components/accessControl/AccessControl";

const { Option } = Select;

const DEPENDENCY_TYPES = [
  { name: "FS", value: 1 },
  { name: "FF", value: 2 },
  { name: "SS", value: 3 },
  { name: "SF", value: 4 },
];

type SelectOption = {
  label: string;
  value: string | number;
};

type NewLine = {
  predecessorTaskId: number;
  successorTaskId: number;
  dependencyTypeId: number;
  lagDays: number;
};
type UpdatedLine = {
  taskDependencyId: number;
  dependencyTypeId?: number;
  lagDays?: number;
};

type DeleteLine = {
  taskDependencyId: number;
};

interface updateTaskDependency {
  projectId: any;
  newLines?: NewLine[];
  updatedLines?: UpdatedLine[];
  deletedLines?: DeleteLine[];
}

interface DependencyRow {
  key: string;
  successorTaskId: number | string | null;
  successorTaskName: string;
  predecessorTaskId: number | string | null;
  predecessorTaskName: string;
  dependencyTypeId: number | string;
  lagDays: number;
  taskDependencyId?: number;
  isEditing?: boolean;
}

const EditableDependencyTable: FC<{
  projectId: any;
  moduleKey: "predecessor" | "successor";
  onRefresh: () => void;
  successorsList: SelectOption[];
  predecessorsList: SelectOption[];
  defaultData?: DependencyRow[];
  taskId: number;
  onCancel: () => void;
}> = ({
  projectId,
  moduleKey,
  predecessorsList,
  successorsList,
  defaultData = [],
  onCancel,
  onRefresh,
  taskId,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [alertMessage, setAlertMessage] = useState<{
    status: "success" | "danger" | undefined;
    message: string;
  }>({
    status: undefined,
    message: "",
  });
  const [data, setData] = useState<DependencyRow[]>(defaultData);
  const [editingKey, setEditingKey] = useState<string | number | null>(null);
  const [originalRow, setOriginalRow] = useState<DependencyRow | null>(null);
  const [initialKeys, setInitialKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    setInitialKeys(new Set(defaultData.map((item) => item.key)));
  }, [defaultData]);

  const isEditing = (record: DependencyRow) => record.key === editingKey;

  const handleEdit = (key: string | number) => {
    const row = data.find((item) => item.key === key);
    if (row) {
      setOriginalRow({ ...row });
      setEditingKey(key);
    }
  };

  const handleCancel = (key: string | number) => {
    const isNew = key?.toString()?.startsWith("new-");
    if (
      isNew &&
      !originalRow?.predecessorTaskId &&
      !originalRow?.successorTaskId
    ) {
      handleDelete(key);
      setOriginalRow(null);
      return;
    }
    setData((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, ...originalRow } : item
      )
    );
    setEditingKey(null);
    setOriginalRow(null);
  };

  const handleSave = (key: string | number) => {
    const row = data.find((item) => item.key == key);
    if (!row) return;

    if (
      (!row.predecessorTaskId && !row.successorTaskId) ||
      !row.dependencyTypeId
    ) {
      setAlertMessage({
        status: "danger",
        message: "Please fill all fields before saving.",
      });
      setTimeout(
        () => setAlertMessage({ status: undefined, message: "" }),
        4000
      );
      return;
    }
    setEditingKey(null);
    setOriginalRow(null);
  };

  const handleDelete = (key: string | number) => {
    setData((prev) => prev.filter((item) => item.key !== key));
    if (editingKey === key) {
      setEditingKey(null);
    }
  };

  const handleAdd = () => {
    if (editingKey) return;

    const newKey = `new-${Date.now()}`;
    setData((prev) => [
      ...prev,
      {
        key: newKey,
        dependencyTypeId: 1,
        successorTaskId: null,
        successorTaskName: "",
        predecessorTaskId: null,
        predecessorTaskName: "",
        lagDays: 0,
        isEditing: true,
      },
    ]);
    setEditingKey(newKey);
  };

  const handleFinalSave = () => {
    setIsSaving(true);
    const updatedLines: UpdatedLine[] = [];
    const newLines: NewLine[] = [];
    const currentKeys = new Set(data.map((item) => item.key));

    for (const row of data) {
      let updateFields: Partial<UpdatedLine> = {};
      const original = defaultData.find((r) => r.key === row.key);
      const isNew = row?.key?.toString()?.startsWith("new-");
      if (!row?.predecessorTaskId && moduleKey == "predecessor") continue;
      if (!row?.successorTaskId && moduleKey == "successor") continue;

      if (isNew) {
        const newObj: NewLine = {
          dependencyTypeId: +row?.dependencyTypeId,
          lagDays: +row?.lagDays,
          predecessorTaskId: taskId, // for moduleKey == "successor"
          successorTaskId: +(row?.successorTaskId ?? 0), // for moduleKey == "successor"
        };
        if (moduleKey === "predecessor") {
          newObj.predecessorTaskId = +(row?.predecessorTaskId ?? 0); // for moduleKey == "predecessor"
          newObj.successorTaskId = taskId; // for moduleKey == "predecessor"
        }
        newLines.push(newObj);
      } else {
        if (original?.dependencyTypeId !== row.dependencyTypeId)
          updateFields.dependencyTypeId = +row.dependencyTypeId;
        if (original?.lagDays !== row.lagDays)
          updateFields.lagDays = row.lagDays;

        if (Object.keys(updateFields).length > 0) {
          updatedLines.push({
            ...updateFields,
            taskDependencyId: +row?.key,
          });
        }
      }
    }

    const deletedLines: DeleteLine[] = Array.from(initialKeys)
      .filter((key) => !currentKeys.has(key))
      ?.map((key) => ({ taskDependencyId: +key }));
    const payload: updateTaskDependency = {
      projectId: projectId,
      newLines,
      updatedLines,
      deletedLines,
    };

    submitTaskDependency(payload);
  };

  const handleChange = (
    key: string,
    field: keyof DependencyRow,
    value: any
  ) => {
    setData((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, [field]: value } : item
      )
    );
  };

  const submitTaskDependency = async (payload: updateTaskDependency) => {
    try {
      const apiResponse = await tasksApis.updateTaskDependency(payload);
      const { status, message: apiMessage, error, data } = apiResponse;
      if (status) {
        onRefresh();
        setAlertMessage({ status: "success", message: data?.message || "" });
        setTimeout(() => {
          setAlertMessage({ status: undefined, message: "" });
          onCancel();
        }, 3000);
      } else {
        setAlertMessage({
          status: "danger",
          message: error + ": " + apiMessage,
        });
        setTimeout(() => {
          setAlertMessage({ status: undefined, message: "" });
        }, 9000);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsSaving(false);
    }
  };

  const configColumns = (data: DependencyRow[]) => {
    return [
      {
        title: "Predecessor",
        dataIndex: "predecessorTaskName",
        key: "predecessorTaskName",
        hidden: moduleKey == "successor",
        render: (_: any, record: DependencyRow) => {
          const existingTask = data
            .filter(
              (item: DependencyRow) =>
                item.predecessorTaskId != record.predecessorTaskId
            )
            ?.map((item: DependencyRow) => item?.predecessorTaskId?.toString());
          const filteredTasks = predecessorsList.filter(
            (option: SelectOption) => {
              return !existingTask?.includes(option?.value?.toString());
            }
          );
          return isEditing(record) ? (
            record.taskDependencyId ? (
              record.predecessorTaskName
            ) : (
              <Select
                value={record.predecessorTaskName}
                showSearch
                optionFilterProp="children"
                onChange={(value, option: any) => {
                  handleChange(record.key, "predecessorTaskId", value);
                  handleChange(
                    record.key,
                    "predecessorTaskName",
                    option?.children
                  );
                }}
                style={{ width: "100%", marginBottom: "3px" }}
              >
                {filteredTasks?.map((task) => (
                  <Option key={task.value} value={task.value}>
                    {task.label}
                  </Option>
                ))}
              </Select>
            )
          ) : (
            record.predecessorTaskName
          );
        },
      },
      {
        title: "Successor",
        dataIndex: "successorTaskName",
        key: "successorTaskName",
        hidden: moduleKey == "predecessor",
        render: (_: any, record: DependencyRow) => {
          const existingTask = data
            .filter(
              (item: DependencyRow) =>
                item.successorTaskId != record.successorTaskId
            )
            ?.map((item: DependencyRow) => item?.successorTaskId?.toString());
          const filteredTasks = successorsList.filter(
            (option: SelectOption) => {
              return !existingTask?.includes(option?.value?.toString());
            }
          );
          return isEditing(record) ? (
            record.taskDependencyId ? (
              record.successorTaskName
            ) : (
              <Select
                value={record.successorTaskName}
                showSearch
                optionFilterProp="children"
                onChange={(value, option: any) => {
                  handleChange(record.key, "successorTaskId", value);
                  handleChange(
                    record.key,
                    "successorTaskName",
                    option?.children
                  );
                }}
                style={{ width: "100%", marginBottom: "3px" }}
              >
                {filteredTasks?.map((task) => (
                  <Option key={task.value} value={task.value}>
                    {task.label}
                  </Option>
                ))}
              </Select>
            )
          ) : (
            record.successorTaskName
          );
        },
      },
      {
        title: "Dependency",
        dataIndex: "dependencyTypeId",
        key: "dependencyTypeId",
        width: data?.length == 0 ? 120 : 80,
        render: (_: any, record: DependencyRow) =>
          isEditing(record) ? (
            <Select
              value={record.dependencyTypeId}
              style={{ width: "100%" }}
              onChange={(value) =>
                handleChange(record.key, "dependencyTypeId", value)
              }
            >
              {DEPENDENCY_TYPES?.map(
                (type: { name: string; value: number }) => (
                  <Option key={type.value} value={type.value}>
                    {type.name}
                  </Option>
                )
              )}
            </Select>
          ) : (
            DEPENDENCY_TYPES.find(
              (type) => type.value == record.dependencyTypeId
            )?.name
          ),
      },
      {
        title: "Lag Days",
        dataIndex: "lagDays",
        key: "lagDays",
        width: data?.length == 0 ? 120 : 80,
        render: (_: any, record: DependencyRow) =>
          isEditing(record) ? (
            <InputNumber
              value={record.lagDays}
              style={{ width: "100%" }}
              step={1}
              parser={(value: any) => parseInt(value, 10)}
              min={-1000}
              max={1000}
              onChange={(value) =>
                handleChange(record.key, "lagDays", value ?? 0)
              }
            />
          ) : (
            record.lagDays
          ),
      },
      {
        title: "Actions",
        key: "actions",
        hidden: data?.length == 0,
        width: 100,
        render: (_: any, record: DependencyRow) => {
          const editable = isEditing(record);
          return editable ? (
            <Space>
              <EditAccess moduleName="Tasks">
                <Button
                  size="small"
                  icon={<SaveOutlined />}
                  onClick={() => handleSave(record.key)}
                />
              </EditAccess>
              {/* <CancelAccess moduleName="Tasks"> */}
                <Button
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={() => handleCancel(record.key)}
                />
              {/* </CancelAccess> */}
            </Space>
          ) : (
            <Space>
              <EditAccess moduleName="Tasks">
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  disabled={!!editingKey}
                  onClick={() => handleEdit(record.key)}
                />
              </EditAccess>
              <CancelAccess moduleName="Tasks">
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={!!editingKey}
                  onClick={() => handleDelete(record.key)}
                />
              </CancelAccess>
            </Space>
          );
        },
      },
    ];
  };
  return (
    <div>
      <Table
        className="customTable"
        columns={configColumns(data)}
        dataSource={data}
        pagination={false}
        bordered
        rowKey="key"
        locale={{
          emptyText: (
            <>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <>
                    <strong>
                      No Tasks Found, Please{" "}
                      <a
                        onClick={handleAdd}
                        className="primary"
                        style={{
                          fontSize: 16,
                          cursor: "pointer",
                        }}
                      >
                        Add{" "}
                        {moduleKey == "predecessor"
                          ? "Predecessor"
                          : "Successor"}
                        .
                      </a>
                    </strong>
                  </>
                }
              />
            </>
          ),
        }}
      />
      {data.length != 0 && !alertMessage?.status && (
        <AddAccess moduleName="Tasks">
          <Button
            block
            icon={<PlusOutlined />}
            onClick={handleAdd}
            style={{ marginTop: 16 }}
            disabled={!!editingKey}
          >
            Add {moduleKey == "predecessor" ? "Predecessor" : "Successor"}
          </Button>
        </AddAccess>
      )}
      {!!alertMessage?.status ? (
        <Alert
          isOpen
          color={alertMessage?.status}
          className="start-0 w-100 py-1 mt-2"
          style={{
            top: 0,
            textAlign: "left",
            fontSize: "14px",
            cursor: "default",
          }}
        >
          <Icon name={"info"} style={{ top: "0.6rem" }} />
          <ReactTyped
            strings={[alertMessage?.message || ""]}
            typeSpeed={10}
            showCursor={false}
          />
        </Alert>
      ) : (
        <></>
      )}
      {!alertMessage?.status ? (
        <Space style={{ marginTop: 12 }}>
          <EditAccess moduleName="Tasks">
            <Button
              type="primary"
              size="small"
              loading={isSaving}
              disabled={
                !!editingKey ||
                (defaultData?.length == 0 && data?.length == 0)
              }
              onClick={handleFinalSave}
            >
              Save
            </Button>
          </EditAccess>
          <Button size="small" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
        </Space>
      ) : (
        <></>
      )}
    </div>
  );
};

export default EditableDependencyTable;
